import React, { useState } from 'react';
import { AppScenario, ScenarioState } from '../data/mock-data';
import { DateRangeFilter } from '../lib/photoFilters';
import {
  filterPhotosBySummaryBucket,
  getPhotoStatusHelp,
  getPhotoSummaryCounts,
  photoSummaryLabels,
  type PhotoSummaryBucket,
} from '../lib/photoStatus';

interface UploadPanelProps {
  scenarioState: AppScenario;
  rootScenario: ScenarioState;
  onScenarioChange: (scenario: ScenarioState) => void;
  onSelectPhoto?: (id: string) => void;
  dateRange?: DateRangeFilter;
  onDateRangeChange?: (range: DateRangeFilter) => void;
  onDateRangeReset?: () => void;
  onUpload?: (files: File[], source: UploadSource) => void;
  onRetryUpload?: () => void;
  canRetryUpload?: boolean;
  onDeletePhoto?: (id: string) => void;
  deletingPhotoId?: string | null;
  isFilteredEmpty?: boolean;
  mode?: 'live' | 'preview';
  showScenarioControls?: boolean;
  variant?: 'panel' | 'top';
}

export type UploadSource = 'library' | 'camera';

const scenarioLabels: Record<ScenarioState, string> = {
  ready: '샘플 기록',
  loading: '불러오는 중',
  empty: '기록 없음',
  error: '연결 오류',
};

const statusLabels: Record<string, string> = {
  ready: '위치 확인됨',
  processing: '등록 중',
  failed: '처리 실패',
  missing_gps: '위치 없음',
  error: '오류',
  completed: '완료',
};

export const UploadPanel: React.FC<UploadPanelProps> = ({
  scenarioState,
  rootScenario,
  onScenarioChange,
  onSelectPhoto,
  dateRange = { start: '', end: '' },
  onDateRangeChange,
  onDateRangeReset,
  onUpload,
  onRetryUpload,
  canRetryUpload = false,
  onDeletePhoto,
  deletingPhotoId = null,
  isFilteredEmpty = false,
  mode = 'preview',
  showScenarioControls = mode !== 'live',
  variant = 'panel',
}) => {
  const [openRecordBucket, setOpenRecordBucket] = useState<PhotoSummaryBucket | null>(null);
  const isTop = variant === 'top';
  const missingGpsPhotos = scenarioState.photos.filter((p) => p.status === 'missing_gps');
  const failedPhotos = scenarioState.photos.filter((p) => p.status === 'failed');
  const missingGps = missingGpsPhotos.length;
  const currentUploadFailed = scenarioState.upload.status === 'error';
  const uploadFailureCount = failedPhotos.length + (currentUploadFailed ? 1 : 0);
  const uploadIssueLabel = currentUploadFailed && failedPhotos.length === 0 ? '등록 실패' : '처리 실패';
  const heading = mode === 'live' ? '현장 사진 등록' : '샘플 현장 기록';
  const summaryCounts = getPhotoSummaryCounts(scenarioState.photos);
  const statItems = [
    { label: photoSummaryLabels.all, value: summaryCounts.all, key: 'all' as const },
    { label: photoSummaryLabels.mapped, value: summaryCounts.mapped, key: 'mapped' as const },
    { label: photoSummaryLabels.missingLocation, value: missingGps, key: 'missingLocation' as const },
    { label: uploadIssueLabel, value: uploadFailureCount, key: 'attention' as const, tone: 'danger' },
  ];
  const hasDateRange = Boolean(dateRange.start || dateRange.end);
  const formatCapturedAt = (value: string | null) => {
    if (!value) return '촬영시각 없음';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '촬영시각 없음';

    return new Intl.DateTimeFormat('ko-KR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const renderStatusBadge = (status: string) => (
    <span className={`status-badge status-badge--${status}`}>
      {statusLabels[status] ?? status.replace('_', ' ')}
    </span>
  );

  const renderUploadPicker = (
    label: string,
    source: UploadSource,
    className = 'button button--primary',
  ) => (
    onUpload ? (
      <label className={className} style={{ cursor: 'pointer' }}>
        {label}
        <input
          type="file"
          accept="image/*"
          capture={source === 'camera' ? 'environment' : undefined}
          multiple={source === 'library'}
          style={{ display: 'none' }}
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            if (files.length > 0) {
              onUpload(files, source);
            }
            e.target.value = '';
          }}
        />
      </label>
    ) : null
  );

  const renderUploadPickers = (
    libraryLabel = '기존 사진 선택',
    cameraLabel = '지금 촬영',
    className = 'button button--primary',
  ) => (
    onUpload ? (
      <>
        {renderUploadPicker(libraryLabel, 'library', className)}
        {renderUploadPicker(cameraLabel, 'camera', 'button button--secondary')}
      </>
    ) : null
  );

  const renderUploadStatus = () => {
    if (scenarioState.upload.status === 'loading') {
      return (
        <div className="upload-status-note upload-status-note--loading" role="status">
          <strong>등록 중</strong>
          <span>{scenarioState.upload.helper}</span>
        </div>
      );
    }

    if (scenarioState.upload.status !== 'error') return null;

    return (
      <div className="upload-status-note upload-status-note--error" role="alert">
        <strong>업로드 실패</strong>
        <span>{scenarioState.upload.helper}</span>
        <div className="button-row">
          <button
            className="button button--secondary"
            type="button"
            disabled={!canRetryUpload}
            onClick={onRetryUpload}
          >
            마지막 사진 다시 시도
          </button>
          {renderUploadPickers('다른 사진 선택', '지금 촬영', 'button button--secondary')}
        </div>
      </div>
    );
  };

  const summaryButtons = (
    <div className="stat-grid" aria-label="등록 요약">
      {statItems.map((item) => (
        <button
          className={`stat-tile ${item.tone === 'danger' && uploadFailureCount > 0 ? 'stat-tile--danger' : ''}`}
          key={item.label}
          type="button"
          aria-expanded={openRecordBucket === item.key}
          onClick={() => {
            setOpenRecordBucket((current) => current === item.key ? null : item.key);
          }}
        >
          <span className="meta-label">{item.label}</span>
          <strong>{item.value}</strong>
        </button>
      ))}
    </div>
  );

  const renderRecordList = () => {
    if (!openRecordBucket) return null;

    const listedPhotos = filterPhotosBySummaryBucket(scenarioState.photos, openRecordBucket);
    const title = photoSummaryLabels[openRecordBucket];
    const showsCurrentFailure = currentUploadFailed && openRecordBucket === 'attention';
    const emptyText = isFilteredEmpty
      ? '선택한 기간에 등록된 사진이 없습니다.'
      : openRecordBucket === 'mapped'
      ? '지도 위치를 확인한 사진이 없습니다.'
      : openRecordBucket === 'missingLocation'
      ? '위치 없는 사진이 없습니다.'
      : openRecordBucket === 'attention'
      ? '처리 실패 사진이 없습니다.'
      : '등록된 사진이 없습니다.';

    return (
      <div className="photo-record-list" role="region" aria-label={`${title} 목록`}>
        <div className="photo-record-list__header">
          <strong>{title}</strong>
          <button
            className="button button--secondary photo-record-list__close"
            type="button"
            onClick={() => setOpenRecordBucket(null)}
          >
            닫기
          </button>
        </div>
        {showsCurrentFailure && (
          <div className="upload-status-note upload-status-note--error" role="alert">
            <strong>최근 등록 실패</strong>
            <span>{scenarioState.upload.helper}</span>
            <div className="button-row">
              <button
                className="button button--secondary"
                type="button"
                disabled={!canRetryUpload}
                onClick={onRetryUpload}
              >
                마지막 사진 다시 시도
              </button>
              {renderUploadPickers('다른 사진 선택', '지금 촬영', 'button button--secondary')}
            </div>
          </div>
        )}
        {listedPhotos.length === 0 && !showsCurrentFailure ? (
          <div className="photo-record-empty" role="status">
            <strong>{emptyText}</strong>
            {isFilteredEmpty && onDateRangeReset && (
              <button className="button button--secondary" type="button" onClick={onDateRangeReset}>
                기간 초기화
              </button>
            )}
          </div>
        ) : listedPhotos.length > 0 ? (
          listedPhotos.map((photo) => (
            <div className="photo-record-item" key={photo.id}>
              <button className="photo-record-item__main" type="button" onClick={() => onSelectPhoto?.(photo.id)}>
                <span>{formatCapturedAt(photo.capturedAt)}</span>
                <span className="muted">{getPhotoStatusHelp(photo)}</span>
              </button>
              <div className="photo-record-item__actions">
                {renderStatusBadge(photo.status)}
                {(photo.status === 'missing_gps' || photo.status === 'failed') && onDeletePhoto && (
                  <button
                    className="button button--secondary photo-record-item__delete"
                    type="button"
                    disabled={deletingPhotoId === photo.id}
                    onClick={() => onDeletePhoto(photo.id)}
                  >
                    {deletingPhotoId === photo.id ? '삭제 중' : '사진 삭제'}
                  </button>
                )}
              </div>
            </div>
          ))
        ) : null}
      </div>
    );
  };

  return (
    <section
      className={`panel upload-panel ${isTop ? 'upload-panel--top' : 'upload-panel--panel'}`}
      aria-label={isTop ? '현장 사진 등록 요약' : undefined}
      aria-labelledby={isTop ? undefined : 'upload-heading'}
    >
      {!isTop && (
        <div className="upload-panel__intro">
          <p className="eyebrow">등록</p>
          <h2 id="upload-heading">{heading}</h2>
          <p className="muted">{scenarioState.upload.helper}</p>
        </div>
      )}
      {isTop && summaryButtons}
      <div className="upload-card">
        <div className="button-row">
          {renderUploadPickers() ?? (
            <div className="upload-demo-note" role="status">
              <strong>샘플 화면</strong>
              <span>실제 등록은 로그인된 실시간 환경에서 진행됩니다.</span>
            </div>
          )}
        </div>
        {renderUploadStatus()}
        {showScenarioControls && (
          <div>
            <p className="eyebrow">샘플 상태</p>
            <div className="state-toggle" role="toolbar" aria-label="샘플 상태 미리보기">
              {(Object.entries(scenarioLabels) as [ScenarioState, string][]).map(([key, label]) => (
                <button
                  key={key}
                  className="toggle"
                  type="button"
                  aria-pressed={rootScenario === key}
                  onClick={() => onScenarioChange(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {isTop && (
        <div className="date-filter" aria-label="기간 조회">
          <span className="date-filter__label">기간</span>
          <label>
            <span className="sr-only">시작일</span>
            <input
              type="date"
              value={dateRange.start}
              max={dateRange.end || undefined}
              onChange={(event) => onDateRangeChange?.({ ...dateRange, start: event.target.value })}
            />
          </label>
          <span className="date-filter__separator">~</span>
          <label>
            <span className="sr-only">종료일</span>
            <input
              type="date"
              value={dateRange.end}
              min={dateRange.start || undefined}
              onChange={(event) => onDateRangeChange?.({ ...dateRange, end: event.target.value })}
            />
          </label>
          <button
            className="button button--secondary date-filter__reset"
            type="button"
            disabled={!hasDateRange}
            onClick={onDateRangeReset}
          >
            초기화
          </button>
        </div>
      )}
      {!isTop && summaryButtons}
      {renderRecordList()}
      {!isTop && scenarioState.jobs.length > 0 ? (
        <ul className="job-list" aria-label="최근 등록 처리 기록">
          {scenarioState.jobs.map((job, index) => (
            <li key={job.id}>
              <strong>등록 묶음 {index + 1}</strong> {renderStatusBadge(job.status)}
              <span className="muted">
                {job.totalCount}장 중 {job.successCount}장 등록
              </span>
            </li>
          ))}
        </ul>
      ) : !isTop ? (
        <div className="photo-list-empty" role="status">
          <strong>
            {rootScenario === 'loading'
              ? '등록 기록을 불러오는 중'
              : rootScenario === 'error'
              ? '등록 기록을 불러오지 못했습니다'
              : '아직 등록된 현장 사진이 없습니다'}
          </strong>
          <p>{scenarioState.upload.helper}</p>
        </div>
      ) : null}
    </section>
  );
};
